/**
 * Shotlist Store
 *
 * Manages shotlist shots, project cast, project props bank, column configuration, and preset banks.
 * Updated via realtime broadcast events for real-time sync.
 */

import { create } from 'zustand';
import { getShotlist } from '../services/visionShotlistClient.js';

export const useShotlistStore = create((set, get) => ({
  shots: [],
  cast: [],
  projectProps: [],
  visibleColumns: [],
  columnOrder: [],
  presets: { frameSize: [], angle: [], cameraMovement: [], equipment: [] },
  isLoading: false,
  isLoaded: false,

  /**
   * Load all shotlist data for a project in one request.
   */
  loadShotlist: async (projectId) => {
    if (get().isLoading) return;
    set({ isLoading: true });
    try {
      const data = await getShotlist(projectId);

      set({
        shots: data.shots || [],
        cast: data.cast || [],
        projectProps: data.props || [],
        visibleColumns: data.config?.visible_columns || [],
        columnOrder: data.config?.column_order || [],
        presets: data.presets || { frameSize: [], angle: [], cameraMovement: [], equipment: [] },
        isLoading: false,
        isLoaded: true,
      });
    } catch (error) {
      console.error('[ShotlistStore] Load error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Clear shotlist state
   */
  clearShotlist: () => {
    set({
      shots: [],
      cast: [],
      projectProps: [],
      visibleColumns: [],
      columnOrder: [],
      isLoading: false,
      isLoaded: false,
    });
  },

  // ─── Shots ───

  addShots: (newShots) => {
    set((state) => {
      const existingIds = new Set(state.shots.map(s => s.id));
      const unique = newShots.filter(s => !existingIds.has(s.id));
      return { shots: [...state.shots, ...unique] };
    });
  },

  updateShot: (shotId, updates) => {
    set((state) => ({
      shots: state.shots.map(s => s.id === shotId ? { ...s, ...updates } : s),
    }));
  },

  removeShot: (shotId) => {
    set((state) => ({
      shots: state.shots.filter(s => s.id !== shotId),
    }));
  },

  reorderShots: (shotIds) => {
    set((state) => {
      const updated = state.shots.map(s => {
        const idx = shotIds.indexOf(s.id);
        return idx !== -1 ? { ...s, sort_order: idx } : s;
      });
      updated.sort((a, b) => a.sort_order - b.sort_order);
      return { shots: updated };
    });
  },

  // ─── Cast ───

  addCast: (castMember) => {
    set((state) => {
      if (state.cast.some(c => c.id === castMember.id)) return state;
      return { cast: [...state.cast, castMember] };
    });
  },

  updateCast: (castMember) => {
    set((state) => ({
      cast: state.cast.map(c => c.id === castMember.id ? { ...c, ...castMember } : c),
    }));
  },

  removeCast: (castId) => {
    set((state) => ({
      cast: state.cast.filter(c => c.id !== castId),
      // Also remove from shots
      shots: state.shots.map(s => ({
        ...s,
        cast_ids: (s.cast_ids || []).filter(id => id !== castId),
      })),
    }));
  },

  // ─── Project Props Bank ───

  addProp: (prop) => {
    set((state) => {
      if (state.projectProps.some(p => p.id === prop.id)) return state;
      return { projectProps: [...state.projectProps, prop] };
    });
  },

  removeProp: (propId, propName) => {
    set((state) => ({
      projectProps: state.projectProps.filter(p => p.id !== propId),
      // Also remove from shots
      shots: state.shots.map(s => ({
        ...s,
        props: (s.props || []).filter(p => p !== propName),
      })),
    }));
  },

  // ─── Custom Presets ───

  addPreset: (preset) => {
    set((state) => {
      const category = preset.category;
      const current = state.presets[category] || [];
      if (current.some(p => p.value === preset.value)) return state;
      return {
        presets: {
          ...state.presets,
          [category]: [...current, { value: preset.value, label: preset.label, custom: true }],
        },
      };
    });
  },

  // ─── Config ───

  setVisibleColumns: (columns) => {
    set({ visibleColumns: columns });
  },

  setColumnOrder: (order) => {
    set({ columnOrder: order });
  },
}));
